/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-magic-numbers */

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Bar,
  ReferenceLine,
  LabelList
} from 'recharts';
import { AxisDomain } from 'recharts/types/util/types';

import { ForecastListItem } from '@futrli-sdk/predict';
import { ConsolidatedForecastListItem } from '@futrli-sdk/consolidations';
import { useIntl } from '@futrli/localisation';

import {
  AxisOptions,
  DataField,
  FinData,
  FinFieldOptions,
  LineBarData,
  PeriodType,
  ROUNDING_OPTIONS,
  SCALING_OPTIONS,
  VALUE_DISPLAY_OPTIONS,
  WIDGET_TYPES
} from '../../types';
import { getDataForLineBarChart } from '../../utils-data';
import { getTextWidth } from '../../utils-generic';
import { addOpacityToColor, generateDefaultColors, getTextColorBasedOnBackground } from '../../utils-colors';
import { chartLabelFontFamily, chartLabelFontSize, chartLabelFontWeight, noDataColors } from '../../constants';
import { BarLineCustomLegend } from './bar-line-custom-legend';
import { isBeforeCurrentPeriodDate } from '../../utils-dates';
import { ForecastSeparatorLabel } from './forecast-separator-label';

import styles from './report-charts.scss';
import { useI18nConstants } from '../../use-i18n-constants';

interface Props {
  data: (FinData | undefined)[];
  fields: DataField[];
  finFieldsOptions: { ref: string; options: FinFieldOptions | undefined }[];
  selectedForecasts: (ForecastListItem | ConsolidatedForecastListItem)[];
  primaryAxisOptions: AxisOptions | undefined;
  secondaryAxisOptions: AxisOptions | undefined;
  currency: string;
  widgetType: WIDGET_TYPES;
  layoutWidth: number;
  layoutHeight: number;
  ariaLabel?: string;
  showZeroLine?: boolean;
  showForecastStartLine?: boolean;
  forecastStartLineColors?: { id: string; color: string }[];
  showBarLabels?: boolean;
  periodType: PeriodType;
}

const getAxisDomain = (
  axisOptions?: AxisOptions,
  data?: Record<string, number | string>[],
  keys?: string[]
): AxisDomain | undefined => {
  // User selected "min_max"
  if (axisOptions?.scaling === SCALING_OPTIONS.MIN_MAX) {
    if (axisOptions.min === 0 && axisOptions.max === 0) {
      return undefined; // fallback to auto behavior
    }
    return [
      typeof axisOptions.min === 'number' ? axisOptions.min : 'dataMin',
      typeof axisOptions.max === 'number' ? axisOptions.max : 'dataMax'
    ];
  }

  // By default scaling is set to "auto" → default is [0, 'auto']
  const hasNegative = data?.some(row => keys?.some(key => typeof row[key] === 'number' && (row[key] as number) < 0));

  // Auto scaling fallback: data contains negative values, using dataMin/dataMax instead
  if (hasNegative) {
    return ['dataMin', 'dataMax']; // Avoid infinite loop crash
  }

  return [0, 'auto'];
};

export const ReportBarLineChart: FC<Props> = ({
  data,
  fields,
  finFieldsOptions,
  selectedForecasts,
  primaryAxisOptions,
  secondaryAxisOptions,
  currency,
  widgetType,
  ariaLabel,
  showZeroLine,
  showForecastStartLine,
  forecastStartLineColors = [],
  showBarLabels,
  layoutWidth,
  layoutHeight,
  periodType
}: Props) => {
  const hasData = !!(data[0] && data[0].periods.length);
  const intl = useIntl();
  const { roundAndFormatValue } = useI18nConstants();
  const [displayBarLabels, setDisplayBarLabels] = useState(false);
  const [ticksNumber, setTicksNumber] = useState(10);

  const firstFinField = useMemo(() => (data.length && data[0]?.fields ? Object.keys(data[0]?.fields)[0] : undefined), [
    data
  ]);

  const predictionsStartDateByForecast = useMemo(() => {
    if (!showForecastStartLine || !data[0]?.periods?.length || !selectedForecasts?.length) {
      return {};
    }
    const result: Record<string, string> = {};
    const increment = firstFinField
      ? (data[0]?.field_meta[firstFinField]?.column_meta?.length || 0) / data[0]!.periods.length
      : data[0]!.periods.length;
    const forecasts =
      (data[0].columns?.length || 0) > selectedForecasts.length
        ? (data[0].columns
            ?.slice(0, selectedForecasts.length)
            .map(
              (cf, i) =>
                selectedForecasts.find(sf => sf.name === (cf === 'Tracker' ? 'Daily tracker forecast' : cf)) ||
                selectedForecasts[i]
            ) as (ForecastListItem | ConsolidatedForecastListItem)[])
        : selectedForecasts;
    let lastPeriod = '';
    forecasts.forEach((sf, fi) => {
      data[0]?.periods.some((period, i) => {
        const date = firstFinField
          ? data[0]?.field_meta[firstFinField]?.column_meta[(fi % increment) + i * increment]?.start_date
          : '';
        if (date && !isBeforeCurrentPeriodDate(date, sf.forecast_from_date || '', periodType)) {
          if (i > 0) {
            result[sf.id] = lastPeriod;
          }
          return true;
        }
        lastPeriod = period;
        return false;
      });
    });
    return result;
  }, [showForecastStartLine, data, firstFinField, selectedForecasts, periodType]);

  useEffect(() => {
    setDisplayBarLabels(
      !!showBarLabels &&
        [
          WIDGET_TYPES.BAR_HORIZONTAL,
          WIDGET_TYPES.BAR_MULTI_HORIZONTAL,
          WIDGET_TYPES.BAR_VERTICAL,
          WIDGET_TYPES.BAR_MULTI_VERTICAL
        ].includes(widgetType)
    );
  }, [widgetType, layoutHeight, layoutWidth, showBarLabels]);

  useEffect(() => {
    setTicksNumber(
      widgetType === WIDGET_TYPES.BAR_HORIZONTAL || widgetType === WIDGET_TYPES.BAR_MULTI_HORIZONTAL
        ? layoutWidth / 8
        : layoutHeight / 4
    );
  }, [widgetType, layoutHeight, layoutWidth]);

  const primaryFields = useMemo(() => fields.filter(f => !f.isSecondary), [fields]);
  const secondaryFields = useMemo(() => fields.filter(f => f.isSecondary), [fields]);

  const isHorizontal = [WIDGET_TYPES.BAR_HORIZONTAL, WIDGET_TYPES.BAR_MULTI_HORIZONTAL].includes(widgetType);

  const primaryAxisCallback = useCallback(
    (value: number | string) => {
      let unit = primaryAxisOptions?.unit;
      if (!unit) {
        const isCurrency = primaryFields.some(
          f => data[0]?.field_meta?.[f.ref]?.display_type === VALUE_DISPLAY_OPTIONS.CURRENCY
        );
        const isPercentage = primaryFields.some(
          f => data[0]?.field_meta?.[f.ref]?.display_type === VALUE_DISPLAY_OPTIONS.PERCENTAGE
        );
        unit = isCurrency
          ? VALUE_DISPLAY_OPTIONS.CURRENCY
          : isPercentage
          ? VALUE_DISPLAY_OPTIONS.PERCENTAGE
          : VALUE_DISPLAY_OPTIONS.NUMBER;
      }
      return roundAndFormatValue(
        value,
        primaryAxisOptions?.rounding || ROUNDING_OPTIONS.INTEGER,
        unit as VALUE_DISPLAY_OPTIONS,
        intl,
        currency
      );
    },
    [primaryAxisOptions?.unit, primaryAxisOptions?.rounding, roundAndFormatValue, intl, currency, primaryFields, data]
  );
  const secondaryAxisCallback = useCallback(
    (value: number | string) => {
      let unit = secondaryAxisOptions?.unit;
      if (!unit) {
        const isCurrency = secondaryFields.some(
          f => data[0]?.field_meta?.[f.ref]?.display_type === VALUE_DISPLAY_OPTIONS.CURRENCY
        );
        const isPercentage = secondaryFields.some(
          f => data[0]?.field_meta?.[f.ref]?.display_type === VALUE_DISPLAY_OPTIONS.PERCENTAGE
        );
        unit = isCurrency
          ? VALUE_DISPLAY_OPTIONS.CURRENCY
          : isPercentage
          ? VALUE_DISPLAY_OPTIONS.PERCENTAGE
          : VALUE_DISPLAY_OPTIONS.NUMBER;
      }
      return roundAndFormatValue(
        value,
        secondaryAxisOptions?.rounding || ROUNDING_OPTIONS.INTEGER,
        unit as VALUE_DISPLAY_OPTIONS,
        intl,
        currency
      );
    },
    [
      secondaryAxisOptions?.unit,
      secondaryAxisOptions?.rounding,
      roundAndFormatValue,
      intl,
      currency,
      secondaryFields,
      data
    ]
  );

  const lineBarData: LineBarData = useMemo(() => {
    const chartColors = generateDefaultColors(primaryFields.length + secondaryFields.length);
    return !data[0] || !data[0].periods.length
      ? {
          colors: [
            {
              key: 'element1',
              ref: 'no-data',
              color: noDataColors[0],
              forecastId: '',
              name: 'element 1',
              type: widgetType
            },
            {
              key: 'element2',
              ref: 'no-data',
              color: noDataColors[1],
              forecastId: '',
              name: 'element 2',
              type: widgetType
            }
          ],
          data: Array.from({ length: 6 }).map((_, i) => ({
            name: `${i}`,
            element1: 1000 + Math.random() * 15000,
            element2: 1000 + Math.random() * 15000
          }))
        }
      : getDataForLineBarChart(
          selectedForecasts,
          primaryFields,
          secondaryFields,
          finFieldsOptions,
          chartColors,
          data,
          widgetType,
          secondaryAxisOptions?.chartType || widgetType
        );
  }, [
    data,
    primaryFields,
    secondaryFields,
    finFieldsOptions,
    selectedForecasts,
    widgetType,
    secondaryAxisOptions?.chartType
  ]);

  const formatTooltip = useCallback(
    (value: number, name: string): string[] => {
      const [ds, ref] = name.split('#|#');
      const label =
        selectedForecasts.length > 1
          ? `${fields.find(f => f.ref === ref)?.name || ref} - ${
              selectedForecasts.find(sf => sf.id === ds)?.name || ds
            }`
          : fields.find(f => f.ref === ref)?.name || ref;

      return [primaryAxisCallback(value), label];
    },
    [primaryAxisCallback, fields, selectedForecasts]
  );

  const renderCustomizedLabel = useCallback(
    (
      color: string, // Background color of the bar to determine label text color
      props: { x: number; y: number; width: number; height: number; value: number } // Position and size of the bar, along with its value
    ) => {
      const { x, y, width, height, value } = props;

      // If bar labels are globally disabled or the value is 0, don't render any labels
      if (!displayBarLabels || !value) {
        return null;
      }

      // Calculate the real width and height depending on the chart's orientation
      const rWidth = isHorizontal ? height : width;
      const rHeight = isHorizontal ? width : height;

      // Get the formatted text to be displayed
      const displayedText = primaryAxisCallback(value);

      // Calculate the pixel width of the text for comparison with the bar's width
      const labelWidth = getTextWidth(
        displayedText,
        `${chartLabelFontWeight} ${chartLabelFontSize}px ${chartLabelFontFamily}`
      );

      // Skip rendering the label if the bar height is too small (for better readability)
      if (Math.abs(rHeight) < 10) {
        return null;
      }

      // Render the label only if the bar width is greater than the label width + padding
      if (rWidth >= labelWidth + 2) {
        // Calculate the position of the label inside the bar
        const labelX = isHorizontal ? x + width + (value < 0 ? 3 : -10) : x + rWidth / 2;
        const labelY = isHorizontal ? y + rWidth / 2 : y + (value < 0 ? -3 : 10);

        return (
          <text
            x={labelX}
            y={labelY}
            fill={getTextColorBasedOnBackground(color)} // Set text color based on the background color
            textAnchor="middle"
            fontSize={`${chartLabelFontSize}px`}
            fontFamily={chartLabelFontFamily}
            fontWeight={chartLabelFontWeight}
            transform={isHorizontal ? `rotate(90, ${labelX}, ${labelY})` : ''} // Rotate the label for horizontal charts
          >
            {displayedText}
          </text>
        );
      } else if (displayBarLabels) {
        // If any bar is too small to display its label, turn off all labels
        setDisplayBarLabels(false); // This triggers a rerender to hide all labels
      }

      // If no conditions are met, render nothing
      return null;
    },
    [primaryAxisCallback, displayBarLabels, isHorizontal]
  );

  return (
    <div className={clsx(styles.chartAndLegendWrapper)}>
      <div className={clsx(styles.chartWrapper)}>
        <ResponsiveContainer
          width="100%"
          height="100%"
          style={{ overflow: 'visible' }}
          initialDimension={{ width: 1, height: 1 }}
          aria-label={ariaLabel}
        >
          <ComposedChart
            data={lineBarData.data}
            style={{ overflow: 'visible' }}
            barGap={0}
            layout={isHorizontal ? 'vertical' : 'horizontal'}
            margin={{ top: 10 }}
          >
            {isHorizontal ? (
              <>
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={hasData}
                  strokeWidth={2}
                  height={hasData ? 60 : 2}
                  stroke="#E5E5E5"
                  style={{
                    fill: '#000',
                    fontSize: '8',
                    fontWeight: 500,
                    fontFamily: 'SageUI'
                  }}
                />
                <XAxis
                  type="number"
                  xAxisId="bottom"
                  orientation="bottom"
                  tick={hasData}
                  strokeWidth={2}
                  width={hasData ? 30 : 2}
                  stroke="#E5E5E5"
                  tickFormatter={primaryAxisCallback}
                  domain={getAxisDomain(
                    primaryAxisOptions,
                    lineBarData.data,
                    lineBarData.colors.map(c => c.key)
                  )}
                  allowDataOverflow={primaryAxisOptions?.scaling === SCALING_OPTIONS.MIN_MAX}
                  interval={0} // Ensures every tick is displayed
                  tickCount={ticksNumber}
                  padding={{ right: 20 }}
                  style={{
                    fill: '#000',
                    fontSize: '8',
                    fontWeight: 500,
                    fontFamily: 'SageUI'
                  }}
                />
                {showZeroLine && <ReferenceLine xAxisId="bottom" x={0} stroke="#C9C9C9" />}
              </>
            ) : (
              <>
                <XAxis
                  dataKey="name"
                  tick={hasData}
                  strokeWidth={2}
                  height={hasData ? 30 : 2}
                  stroke="#E5E5E5"
                  style={{
                    fill: '#000',
                    fontSize: '8',
                    fontWeight: 500,
                    fontFamily: 'SageUI'
                  }}
                />
                <YAxis
                  yAxisId="left"
                  tick={hasData}
                  strokeWidth={2}
                  width={hasData ? 60 : 2}
                  stroke="#E5E5E5"
                  tickFormatter={primaryAxisCallback}
                  domain={getAxisDomain(
                    primaryAxisOptions,
                    lineBarData.data,
                    lineBarData.colors.map(c => c.key)
                  )}
                  allowDataOverflow={primaryAxisOptions?.scaling === SCALING_OPTIONS.MIN_MAX}
                  interval={0} // Ensures every tick is displayed
                  tickCount={ticksNumber}
                  style={{
                    fill: '#000',
                    fontSize: '8',
                    fontWeight: 500,
                    fontFamily: 'SageUI'
                  }}
                />
                {showZeroLine && <ReferenceLine yAxisId="left" y={0} stroke="#C9C9C9" />}
              </>
            )}
            {secondaryFields.length > 0 &&
              (isHorizontal ? (
                <XAxis
                  type="number"
                  xAxisId="top"
                  orientation="top"
                  tick={hasData}
                  strokeWidth={2}
                  width={hasData ? 30 : 2}
                  stroke="#E5E5E5"
                  tickFormatter={secondaryAxisCallback}
                  domain={getAxisDomain(
                    secondaryAxisOptions,
                    lineBarData.data,
                    lineBarData.colors.map(c => c.key)
                  )}
                  allowDataOverflow={secondaryAxisOptions?.scaling === SCALING_OPTIONS.MIN_MAX}
                  interval={0} // Ensures every tick is displayed
                  tickCount={ticksNumber}
                  padding={{ right: 20 }}
                  style={{
                    fill: '#000',
                    fontSize: '8',
                    fontWeight: 500,
                    fontFamily: 'SageUI'
                  }}
                />
              ) : (
                <YAxis
                  type="number"
                  yAxisId="right"
                  orientation="right"
                  tick={hasData}
                  strokeWidth={2}
                  width={hasData ? 60 : 2}
                  stroke="#E5E5E5"
                  tickFormatter={secondaryAxisCallback}
                  domain={getAxisDomain(
                    primaryAxisOptions,
                    lineBarData.data,
                    lineBarData.colors.map(c => c.key)
                  )}
                  allowDataOverflow={secondaryAxisOptions?.scaling === SCALING_OPTIONS.MIN_MAX}
                  interval={0} // Ensures every tick is displayed
                  tickCount={ticksNumber}
                  style={{
                    fill: '#000',
                    fontSize: '8',
                    fontWeight: 500,
                    fontFamily: 'SageUI'
                  }}
                />
              ))}
            {hasData && (
              <Tooltip
                formatter={formatTooltip}
                wrapperStyle={{ fontFamily: 'SageUI', fontSize: 8, lineHeight: 1, fontWeight: 500 }}
              />
            )}
            {lineBarData.colors?.map(c => {
              switch (c.type) {
                case WIDGET_TYPES.AREA:
                  return (
                    <Area
                      {...(isHorizontal
                        ? { xAxisId: c.isSecondary ? 'top' : 'bottom' }
                        : { yAxisId: c.isSecondary ? 'right' : 'left' })}
                      key={c.key}
                      type="linear"
                      dataKey={c.key}
                      stroke={c.color}
                      strokeWidth={1.5}
                      dot={{ fill: c.color, r: 1.5 }}
                      activeDot={{ strokeWidth: 2, r: 4 }}
                      fill={addOpacityToColor(c.color, 0.2)}
                      isAnimationActive={false}
                    />
                  );
                case WIDGET_TYPES.BAR_MULTI_VERTICAL:
                case WIDGET_TYPES.BAR_VERTICAL:
                case WIDGET_TYPES.BAR_HORIZONTAL:
                case WIDGET_TYPES.BAR_MULTI_HORIZONTAL:
                  return (
                    <Bar
                      {...(isHorizontal
                        ? { xAxisId: c.isSecondary ? 'top' : 'bottom' }
                        : { yAxisId: c.isSecondary ? 'right' : 'left' })}
                      key={c.key}
                      dataKey={c.key}
                      stackId={
                        c.type === WIDGET_TYPES.BAR_VERTICAL || c.type === WIDGET_TYPES.BAR_HORIZONTAL ? c.ref : c.key
                      }
                      fill={addOpacityToColor(c.color, 0.8)}
                      isAnimationActive={false}
                    >
                      {hasData && displayBarLabels && (
                        <LabelList
                          dataKey={c.key}
                          content={props =>
                            renderCustomizedLabel(
                              c.color,
                              props as { x: number; y: number; width: number; height: number; value: number }
                            )
                          }
                        />
                      )}
                    </Bar>
                  );
                default:
                  return (
                    <Line
                      {...(isHorizontal
                        ? { xAxisId: c.isSecondary ? 'top' : 'bottom' }
                        : { yAxisId: c.isSecondary ? 'right' : 'left' })}
                      key={c.key}
                      type={
                        (!c.isSecondary && widgetType === WIDGET_TYPES.LINE_HORIZONTAL) ||
                        (c.isSecondary && secondaryAxisOptions?.chartType === WIDGET_TYPES.LINE_HORIZONTAL)
                          ? 'step'
                          : 'linear'
                      }
                      dataKey={c.key}
                      stroke={c.color}
                      strokeWidth={1.5}
                      dot={{ fill: c.color, r: 1.5 }}
                      activeDot={{ strokeWidth: 2, r: 4 }}
                      isAnimationActive={false}
                    />
                  );
              }
            })}
            {showForecastStartLine &&
              Object.keys(predictionsStartDateByForecast).map(fid => (
                <ReferenceLine
                  key={`separator-${fid}`}
                  position="end"
                  {...(isHorizontal
                    ? { xAxisId: 'bottom', y: predictionsStartDateByForecast[fid] }
                    : { yAxisId: 'left', x: predictionsStartDateByForecast[fid] })}
                  stroke={forecastStartLineColors.find(fsc => fsc.id === fid)?.color || '#000'}
                  strokeDasharray="3 3"
                  label={props => (
                    <ForecastSeparatorLabel
                      fill={forecastStartLineColors.find(fsc => fsc.id === fid)?.color || '#000'}
                      isHorizontal={isHorizontal}
                      {...props}
                    />
                  )}
                />
              ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {hasData && (
        <BarLineCustomLegend
          lineBarData={lineBarData}
          forecasts={selectedForecasts}
          forecastStartLineColors={showForecastStartLine ? forecastStartLineColors : undefined}
        />
      )}
    </div>
  );
};
